import { useState } from 'react';
import { t, Trans } from '@lingui/macro';

import Plus from '@decisiv/iconix/lib/components/Plus';
import Pencil from '@decisiv/iconix/lib/components/Pencil';
import Search from '@decisiv/iconix/lib/components/Search';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import SearchInput from '@decisiv/ui-components/lib/components/SearchInput';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import { Column } from '@decisiv/ui-components/lib/components/Table/types';

import useSort from 'utils/useSort';
import { useCurrentUserContext } from 'utils/User';
import PaginationFooter from 'components/PaginationFooter';
import Link from '@decisiv/ui-components/lib/components/Link';
import Loading from 'components/Loading';
import EmptyResults from 'components/EmptyResults';
import { NoWrapHeaderTable, RightAlignedFlex } from '../components/styles';
import { useManagedDepotsQuery } from './hooks/useManagedDepotsQuery';

type ManagedDepotsType =
  ManagedDepotsQuery['managedAssetGroupings']['collection'][0];
type ColumnProperties = Pick<
  ManagedDepotsType,
  | 'id'
  | 'name'
  | 'totalAssetsEnabled'
  | 'city'
  | 'state'
  | 'status'
  | 'sentinelTeam'
  | 'platformAccount'
  | 'webCaseUrl'
>;

const Depots = () => {
  const [page, setPage] = useState(1);

  const { isSuperUser } = useCurrentUserContext();

  const [{ sort }, { getSortProps }] = useSort();
  const { defaultSort, onSort } = getSortProps();

  const { data, loading } = useManagedDepotsQuery({
    variables: {
      sort,
      page,
    },
  });

  const columns: Column<ColumnProperties>[] = [
    {
      name: 'oem_segment',
      title: t`OEM Segment Name`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{sentinelTeam?.oemSegment?.name || '-'}</span>
      ),
      sortBy: (direction) => onSort('OEM_SEGMENT_NAME', direction),
    },
    {
      name: 'team',
      title: t`Team Name`,
      DataCell: ({ rowData: { sentinelTeam } }) => (
        <span>{sentinelTeam?.name ?? '-'}</span>
      ),
      sortBy: (direction) => onSort('SENTINEL_TEAM_NAME', direction),
    },
    {
      name: 'account',
      title: t`Account Name`,
      DataCell: ({ rowData: { platformAccount } }) => (
        <span>{platformAccount?.name || '-'}</span>
      ),
      sortBy: (direction) => onSort('PLATFORM_ACCOUNT', direction),
    },
    {
      name: 'name',
      title: t`Depot Name`,
      DataCell: ({ rowData: { id, name } }) => (
        <Link to={`/management/depot/${id}`} text={name} />
      ),
      sortBy: (direction) => onSort('NAME', direction),
    },
    {
      name: 'id',
      title: t`Depot ID`,
      DataCell: ({ rowData: { id } }) => (
        <Link to={`/management/depot/${id}`} text={id} />
      ),
      sortBy: (direction) => onSort('ID', direction),
    },
    {
      name: 'assets',
      title: t`Assets`,
      DataCell: ({ rowData: { totalAssetsEnabled } }) => (
        <span>{totalAssetsEnabled}</span>
      ),
      sortBy: (direction) => onSort('ENABLED_ASSET_COUNT', direction),
    },
    {
      name: 'city',
      title: t`City`,
      DataCell: ({ rowData: { city } }) => <span>{city || '-'}</span>,
      sortBy: (direction) => onSort('CITY', direction),
    },
    {
      name: 'state',
      title: t`State`,
      DataCell: ({ rowData: { state } }) => <span>{state || '-'}</span>,
      sortBy: (direction) => onSort('STATE', direction),
    },
    {
      name: 'actions',
      title: t`Actions`,
      fixed: true,
      DataCell: ({ rowData: { webCaseUrl } }) => (
        <Flex flex={1} justifyContent="center" alignContent="center">
          <Button
            size="small"
            kind="secondary"
            icon={Pencil}
            text={t`Edit`}
            disabled={!webCaseUrl}
            onClick={() => {
              if (webCaseUrl) {
                window.open(webCaseUrl, '_blank');
              }
            }}
          />
        </Flex>
      ),
    },
  ];

  const tableData = data?.managedAssetGroupings.collection ?? [];
  const pagination = data?.managedAssetGroupings.pagination;

  return (
    <>
      <Flex marginX={2} marginY={1} justifyContent="space-between">
        {isSuperUser && (
          <Button text={t`Create Depot`} icon={Plus} size="small" />
        )}
        <RightAlignedFlex alignItems="center">
          <P size="small" as="span" shade={1}>
            <Trans>Search Depot by Name</Trans>
          </P>
          <SearchInput name="searchByDepot" size="small" value="" />
        </RightAlignedFlex>
      </Flex>
      {loading && <Loading />}
      {!loading && tableData.length === 0 && (
        <EmptyResults
          title={t`No Depots Found`}
          subtitle={t`No Managed Depots Found`}
          icon={Search}
        />
      )}
      {!loading && tableData.length > 0 && (
        <NoWrapHeaderTable
          scroll="x"
          data={tableData}
          columns={columns}
          onSort={onSort}
          defaultSort={defaultSort}
          footer={() => (
            <PaginationFooter
              title={t`Depots`}
              setCurrentPage={setPage}
              totalOnPage={tableData.length}
              page={pagination?.page}
              perPage={pagination?.perPage}
              totalEntries={pagination?.totalEntries}
              totalPages={pagination?.totalPages}
            />
          )}
        />
      )}
    </>
  );
};

export default Depots;
